/**
 * Release 2 S3 (A) — live updates for an open smart board without a new WS room or event.
 *
 * The relay sends asset events only to the asset room and the rooms of the boards holding the asset, never to a smart
 * board's room (a card there would reach viewers the definition's narrowing hides it from) nor to the workspace room
 * (membership alone authorizes it). So the page joins the rooms of the boards its definition watches — those boards
 * plus, with `include_descendants`, their visible sub-boards from `GET /boards/{id}/tree` — and the section A tap in
 * lib/ws/apply.ts refetches the listing once per burst. A workspace-wide definition (no `board_ids`), a scope above
 * `SMART_ROOM_CAP` rooms or a tree cut at the depth limit has no bounded room set: the listing is refetched every
 * `SMART_POLL_MS` while the tab is visible instead. Every smart board also refetches on window focus and after a socket
 * reconnect. The tree queries live under `['board-tree']`, which board events invalidate, so the room set follows
 * sub-boards created, moved or deleted; rooms are left on unmount and whenever the set changes.
 */
import { useEffect, useMemo } from 'react';
import { useQueries, useQueryClient } from '@tanstack/react-query';
import { api, req } from '@/lib/api/client';
import { keys } from '@/lib/api/keys';
import type { Board, BoardTreeNode } from '@/lib/api/types';
import { useRealtime } from '@/lib/ws/RealtimeProvider';
import { boardRoom } from '@/lib/shared';
import { splitDefinition } from './definition';

/** Leaves headroom under the gateway's 500 rooms per connection for the rest of the app. */
export const SMART_ROOM_CAP = 100;
export const SMART_POLL_MS = 30_000;
const TREE_DEPTH = 8; // the API maximum

/** False when the tree is cut at the depth limit (deeper boards would be missed). */
function collect(n: BoardTreeNode, out: Set<string>): boolean {
  out.add(n.id);
  if (n.has_children && !n.children.length) return false;
  return n.children.every((c) => collect(c, out));
}

/** Board ids whose rooms carry the definition's asset events, or null when the scope is unbounded or too large. */
export function useSmartBoardWatchedIds(board: Board): string[] | null {
  const { boardIds, includeDescendants } = splitDefinition(board.smart_filters as Record<string, unknown> | null | undefined).scope;
  const trees = useQueries({
    queries: (includeDescendants ? boardIds : []).map((id) => ({
      queryKey: [...keys.boardTree(id), TREE_DEPTH, 'smart-scope'],
      // a watched board the viewer cannot open answers 403/404: it is silently not watched (no global error toast)
      queryFn: () => (req(api.GET('/boards/{boardId}/tree', { params: { path: { boardId: id }, query: { depth: TREE_DEPTH } } })) as Promise<BoardTreeNode>).catch(() => null),
      staleTime: 5 * 60_000,
    })),
  });
  let key: string | null = null;
  if (boardIds.length) {
    const out = new Set<string>(includeDescendants ? [] : boardIds);
    let complete = true;
    for (const t of trees) if (t.data) complete = collect(t.data, out) && complete;
    if (complete && out.size <= SMART_ROOM_CAP) key = [...out].sort().join(',');
  }
  return useMemo(() => (key === null ? null : key.split(',').filter(Boolean)), [key]);
}

/** Joins the smart board's own room (definition changes) and its watched boards' rooms; polls when unbounded; refetches on focus / reconnect. */
export function useSmartBoardLive(board: Board): void {
  const rt = useRealtime();
  const qc = useQueryClient();
  const watched = useSmartBoardWatchedIds(board);
  const roomsKey = [board.id, ...(watched ?? [])].map(boardRoom).join(',');
  useEffect(() => {
    if (!rt) return;
    const offs = roomsKey.split(',').map((r) => rt.subscribe(r));
    return () => offs.forEach((off) => off());
  }, [rt, roomsKey]);
  const poll = watched === null;
  useEffect(() => {
    let last = Date.now();
    const refetch = () => {
      if (document.visibilityState !== 'visible' || Date.now() - last < 1000) return; // focus + visibilitychange fire together
      last = Date.now();
      void qc.invalidateQueries({ queryKey: keys.boardAssetsAll(board.id) });
      void qc.invalidateQueries({ queryKey: ['board-facets', board.id] });
    };
    const t = poll ? setInterval(refetch, SMART_POLL_MS) : null;
    // catch up when the tab comes back and after a socket reconnect: an unbounded scope has no replay at all, and a
    // truncated replay of a watched room only refetches that board's own queries (invalidateRoom)
    let dropped = false;
    const offState = rt?.onConnectionState((st) => {
      if (st === 'closed') dropped = true;
      else if (st === 'open' && dropped) {
        dropped = false;
        last = 0;
        refetch();
      }
    });
    const onShow = () => refetch();
    window.addEventListener('focus', onShow);
    document.addEventListener('visibilitychange', onShow);
    return () => {
      if (t) clearInterval(t);
      offState?.();
      window.removeEventListener('focus', onShow);
      document.removeEventListener('visibilitychange', onShow);
    };
  }, [poll, qc, rt, board.id]);
}
